import React, { useCallback } from 'react';
import { map, filter, propEq, assoc } from 'ramda';
import styled, { css } from 'styled-components';
import CollapsibleChevronBlock from '~/components/molecule/CollapsibleChevronBlock';
import JustificationContainer from '~/components/atom/JustificationContainer';
import alterById from '~/util/alterById/index';
import AnimatedCheckbox from '~/components/molecule/AnimatedCheckbox';
import { Body, Heading4, Label, Variant } from '~/components/atom/Typography';
import GridContainer from '~/components/atom/GridContainer';
import withIconTooltip from '~/hocs/withIconTooltip';
import OverflowScrollWrapper from '~/components/molecule/OverflowScrollWrapper';
import KenmerkRow from './components/KenmerkRow';
import { RealworksRelatieSyncType } from '~/graphql/types';
import Icon from '~/components/atom/Icon';
import type { ActiveKenmerkSearch, Kenmerk } from '../Synchronization/types';

const text = {
  kenmerkLabel: 'Relaties met kenmerk',
  tagLabel: 'Krijgen een tag in BNDL',
  [RealworksRelatieSyncType.Full]: {
    realworksKenmerkTooltip: 'Alle relaties worden gesynchroniseerd.',
    tagTooltip:
      'Deze tag wordt aan het contact in BNDL toegevoegd als de gesynchroniseerde relatie dit kenmerk in Realworks heeft.',
    checkboxLegend: null,
  },
  [RealworksRelatieSyncType.LimitToKenmerken]: {
    realworksKenmerkTooltip:
      'Relaties met geselecteerde kenmerken worden gesynchroniseerd.',
    tagTooltip: `Deze tag wordt aan het contact in BNDL toegevoegd als de gesynchroniseerde relatie
    dit kenmerk in Realworks heeft — ook wanneer dit kenmerk hier niet is geselecteerd.`,
    checkboxLegend: 'Aangevinkt = wordt gesynchroniseerd',
  },
  [RealworksRelatieSyncType.FullExcludingKenmerken]: {
    realworksKenmerkTooltip:
      'Relaties met geselecteerde kenmerken worden niet gesynchroniseerd.',
    tagTooltip:
      'Deze tag wordt aan het contact in BNDL toegevoegd als de gesynchroniseerde relatie dit kenmerk in Realworks heeft.',
    checkboxLegend: 'Aangevinkt = wordt uitgesloten',
  },
};

type Field = 'check' | 'tag';

export type Props = {
  /** Group name */
  name: string;
  /** All kenmerken within this group */
  kenmerken: Array<Kenmerk>;
  /** Whether the group is selectable */
  selectable: boolean;
  /** The sync type */
  syncType: RealworksRelatieSyncType;
  /** The function to call when the group is changed */
  onChange: (args: { kenmerkList: Array<Kenmerk> }) => void;
  /** Whether the group is open (controlled) */
  isOpen: boolean;
  /** Callback when the group is toggled */
  onToggle: () => void;
  /** Active search match for scroll and in-text highlighting */
  activeKenmerkSearch?: ActiveKenmerkSearch | null;
  /** Current kenmerk highlighted by the missing-kenmerken walkthrough wizard */
  activeWizardKenmerkId?: number | null;
  /** Kenmerk IDs not yet present in the saved kenmerkMapping */
  missingKenmerkIds?: Array<number>;
};

const KenmerkGroup: React.FCC<Props> = ({
  dataTestId,
  kenmerken,
  name,
  selectable,
  syncType,
  onChange: onParentChange,
  isOpen,
  onToggle,
  activeKenmerkSearch = null,
  activeWizardKenmerkId = null,
  missingKenmerkIds = [],
  ...rest
}) => {
  const checkedAmount: number = filter(
    propEq(true, 'checked'),
    kenmerken,
  ).length;

  const isGroupChecked = checkedAmount === kenmerken.length;
  const partiallyChecked = !isGroupChecked && checkedAmount > 0;

  const onChange = useCallback(
    (id: number, value: string | boolean | null, field: Field) => {
      const updated = alterById(id, field, value, kenmerken);
      onParentChange({ kenmerkList: updated });
    },
    [kenmerken, onParentChange],
  );

  const markAllCheckboxes = useCallback(
    (value: boolean) =>
      onParentChange({ kenmerkList: map(assoc('checked', value), kenmerken) }),
    [kenmerken, onParentChange],
  );

  const hideLabel =
    syncType === RealworksRelatieSyncType.FullExcludingKenmerken &&
    isGroupChecked;

  return (
    <CollapsibleChevronBlock
      data-testid={dataTestId}
      header={
        <AnimatedCheckbox
          label={
            <Label size="base" margin={[null]}>
              {name}
            </Label>
          }
          name={`${name}-group-checkbox`}
          value={isGroupChecked || partiallyChecked}
          icon={partiallyChecked ? 'minus' : undefined}
          selectable={selectable}
          disableOnChangeViaLabel
          onLabelClick={onToggle}
          onChange={e => {
            e.stopPropagation();
            markAllCheckboxes(
              partiallyChecked ? isGroupChecked : !isGroupChecked,
            );
          }}
          appearance="primary"
        />
      }
      variant="primary"
      isOpen={isOpen}
      onToggle={() => onToggle()}
      {...rest}
    >
      <ChildrenContainer style={{ maxHeight: '400px' }}>
        <GridContainer gap="base" columns="1fr 1fr">
          <JustificationContainer direction="column" gap="xxs">
            <LabelWithTooltip
              size="s"
              variant={Variant.primary}
              tooltipMessage={text[syncType].realworksKenmerkTooltip}
            >
              <JustificationContainer direction="row" gap="xxs" align="center">
                <Icon name="users" strokeWidth={2.3} />
                {text.kenmerkLabel}
              </JustificationContainer>
            </LabelWithTooltip>
            {selectable && text[syncType].checkboxLegend && (
              <JustificationContainer direction="row" gap="xxs" align="center">
                <Icon
                  name={
                    syncType === RealworksRelatieSyncType.FullExcludingKenmerken
                      ? 'close'
                      : 'check'
                  }
                  strokeWidth={2.8}
                  color={{
                    group:
                      syncType ===
                      RealworksRelatieSyncType.FullExcludingKenmerken
                        ? 'danger'
                        : 'success',
                  }}
                />
                <Body size="s" variant={Variant.secondary} margin={[null]}>
                  {text[syncType].checkboxLegend}
                </Body>
              </JustificationContainer>
            )}
          </JustificationContainer>
          <JustificationContainer direction="column">
            {!hideLabel && (
              <LabelWithTooltip
                size="s"
                variant={Variant.primary}
                tooltipMessage={text[syncType].tagTooltip}
              >
                <JustificationContainer
                  direction="row"
                  gap="xxs"
                  align="center"
                >
                  <Icon name="arrowRight" strokeWidth={2.3} />
                  <Icon name="tag" strokeWidth={2.3} />
                  {text.tagLabel}
                </JustificationContainer>
              </LabelWithTooltip>
            )}
          </JustificationContainer>

          {isOpen && (
            <>
              {kenmerken.map(({ description, id, checked, tag }) => (
                <KenmerkRow
                  row={{
                    description,
                    id,
                    checked,
                    tag,
                  }}
                  key={`key-${id}`}
                  onChange={(value, field: Field) => onChange(id, value, field)}
                  selectable={selectable}
                  syncType={syncType}
                  isHighlighted={
                    activeKenmerkSearch?.kenmerkId === id ||
                    activeWizardKenmerkId === id
                  }
                  highlightSubstring={
                    activeKenmerkSearch?.kenmerkId === id
                      ? activeKenmerkSearch.highlightSubstring
                      : undefined
                  }
                  isMissing={missingKenmerkIds.includes(id)}
                />
              ))}
            </>
          )}
        </GridContainer>
      </ChildrenContainer>
    </CollapsibleChevronBlock>
  );
};

const LabelWithTooltip = withIconTooltip(Heading4, {
  variant: Variant.primary,
  margin: [null],
});

const ChildrenContainer = styled(OverflowScrollWrapper)<{}>`
  ${({ theme }) => css`
    padding: ${theme.space('m')} ${theme.space('l')};
    background-color: ${theme.color('white')};
    border-radius: ${theme.getTokens().border.radius.base};
  `}
`;

export default KenmerkGroup;
